
import "./ArchivedRuleDetails.css";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams,useNavigate,useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  viewCodeAssistRules,
  clearRuleDraft,
} from "../../ViewRuleDatabase/Redux/ruleDatabaseSlice";
import { setSelectedRuleId } from "../redux/Archivedrulesslice";

export default function ArchivedRuleDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const ruleId = searchParams.get("code_assist_manual_rule_id");

  const selectedRuleId = useSelector(
    (state) => state.archiveRule.selectedRuleId
  );

  const { ruleDraft, ruleDraftLoading, ruleDraftError } = useSelector(
    (state) => state.ruleDatabase
  );

useEffect(() => {
    if (!ruleId) {
      toast.error("No rule selected. Please go back and pick a rule again.");
      return;
    }

    // Keep the archive slice in sync too, since handleEditRule below still reads it
    dispatch(setSelectedRuleId(ruleId));

    dispatch(
      viewCodeAssistRules({ code_assist_manual_rule_id: ruleId })
    )
      .unwrap()
      .then((res) => {
        toast.success(res?.message || "Rule details loaded successfully");
      })
      .catch((err) => {
        toast.error(err || "Failed to load rule details");
      });

    return () => {
      dispatch(clearRuleDraft());
    };
  }, [dispatch, ruleId]);
  const handleBack = () => {
    navigate("/archived-rule");
  };

  const handleEditRule = () => {
   dispatch(setSelectedRuleId(selectedRuleId));
    navigate(`/archived-edit-rule`);
  };

  const ruleInfo = ruleDraft?.rule_information || {};
  const ruleConfig = ruleDraft?.rule_configuration || {};
  const archiveInfo = ruleDraft?.archived_information || {};
  const pathwayEntry = ruleConfig?.pathway_entry || {};
  const triggerCodes = ruleConfig?.trigger_codes || [];

  // Small helper: only the VALUE spot shows loading/error/empty state,
  // the surrounding layout/labels always stay mounted.
  const FieldValue = ({ value, fallback = "—" }) => {
    if (ruleDraftLoading) {
      return <span className="archivedRuleDetails-value--skeleton">Loading...</span>;
    }
    if (ruleDraftError) {
      return <span className="archivedRuleDetails-value--muted">Failed to load</span>;
    }
    return <>{value || fallback}</>;
  };

  return (
    <div className="archivedRuleDetails-page">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header - always rendered */}
      <div className="archivedRuleDetails-header">
        <div className="archivedRuleDetails-header-titleRow">
          <h1 className="archivedRuleDetails-title">
            <FieldValue value={ruleDraft?.description || ruleDraft?.rule_id} />
          </h1>
          <span className="archivedRuleDetails-statusBadge">
            {ruleDraftLoading ? "…" : (ruleDraft?.status || "").toUpperCase()}
          </span>
        </div>
        <div className="archivedRuleDetails-subtitle">
          ID: <FieldValue value={ruleDraft?.rule_id} />
        </div>
      </div>

      {/* Archive Information - always rendered */}
      <section className="archivedRuleDetails-infoCard">
        <div className="archivedRuleDetails-infoCard-headerRow">
          <span className="archivedRuleDetails-infoIcon" aria-hidden="true">
            &#9432;
          </span>
          <span className="archivedRuleDetails-infoTitle">
            Archive Information
          </span>
        </div>
        <p className="archivedRuleDetails-infoText">
          This rule is retained for historical and audit purposes and is
          no longer active.
        </p>

        <div className="archivedRuleDetails-infoMetaRow">
          <span>
            <strong>Status:</strong>{" "}
            <FieldValue value={archiveInfo.status || ruleDraft?.status} />
          </span>
          <span>
            <strong>Archived By:</strong>{" "}
            <FieldValue value={archiveInfo.archived_by} />
          </span>
        </div>
        <div className="archivedRuleDetails-infoMetaRow">
          <span>
            <strong>Archived On:</strong>{" "}
            <FieldValue value={archiveInfo.archived_on} />
          </span>
        </div>
      </section>

      {/* Rule Information - always rendered */}
      <section className="archivedRuleDetails-card">
        <div className="archivedRuleDetails-card-header">
          Rule Information
        </div>
        <div className="archivedRuleDetails-card-body">
          <div className="archivedRuleDetails-grid">
            <div className="archivedRuleDetails-field">
              <div className="archivedRuleDetails-label">Rule ID</div>
              <div className="archivedRuleDetails-value">
                <FieldValue value={ruleDraft?.rule_id} />
              </div>
            </div>
            <div className="archivedRuleDetails-field">
              <div className="archivedRuleDetails-label">Description</div>
              <div className="archivedRuleDetails-value">
                <FieldValue value={ruleDraft?.description} />
              </div>
            </div>

            <div className="archivedRuleDetails-field">
              <div className="archivedRuleDetails-label">Created By</div>
              <div className="archivedRuleDetails-value">
                <FieldValue value={ruleInfo.created_by} />{" "}
                {!ruleDraftLoading && !ruleDraftError && (
                  <span className="archivedRuleDetails-value--muted">
                    ({ruleInfo.created_on})
                  </span>
                )}
              </div>
            </div>

            <div className="archivedRuleDetails-field">
              <div className="archivedRuleDetails-label">Clinical Domain</div>
              <div className="archivedRuleDetails-value">
                <FieldValue value={ruleInfo.clinical_domain} />
              </div>
            </div>

            {ruleInfo.submitted_for_testing && (
              <div className="archivedRuleDetails-field">
                <div className="archivedRuleDetails-label">
                  Submitted For Testing
                </div>
                <div className="archivedRuleDetails-value">
                  {ruleInfo.submitted_for_testing}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Rule Configuration - always rendered */}
      <section className="archivedRuleDetails-card">
        <div className="archivedRuleDetails-card-header">
          Rule Configuration
        </div>
        <div className="archivedRuleDetails-card-body">
          <div className="archivedRuleDetails-field">
            <div className="archivedRuleDetails-label">
              Pathway Entry Point
            </div>
            <span className="archivedRuleDetails-pathwayTag">
              <span
                className="archivedRuleDetails-pathwayIcon"
                aria-hidden="true"
              >
                &#8677;
              </span>
              <FieldValue value={pathwayEntry.value} />
            </span>
          </div>

          <div className="archivedRuleDetails-field archivedRuleDetails-field--spaced">
            <div className="archivedRuleDetails-label">Trigger Codes</div>
            <div className="archivedRuleDetails-codesList">
              {ruleDraftLoading ? (
                <span className="archivedRuleDetails-value--skeleton">
                  Loading...
                </span>
              ) : triggerCodes.length === 0 ? (
                <div className="archivedRuleDetails-value--muted">
                  No trigger codes.
                </div>
              ) : (
                triggerCodes.map((item, idx) => (
                  <div
                    className="archivedRuleDetails-codeRow"
                    key={`${item.code}-${idx}`}
                  >
                    <span className="archivedRuleDetails-codeText">
                      {item.code}
                    </span>
                    <span className="archivedRuleDetails-codeDesc">
                      {item.description}
                      {item.code_type ? ` (${item.code_type})` : ""}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer - always rendered */}
      <div className="archivedRuleDetails-footer">
        <button
          type="button"
          className="archivedRuleDetails-backLink"
          onClick={handleBack}
        >
          Back to Archived Rules
        </button>
        <button
          type="button"
          className="archivedRuleDetails-editBtn"
          onClick={handleEditRule}
        >
          <span
            className="archivedRuleDetails-editBtn-icon"
            aria-hidden="true"
          >
            &#9998;
          </span>
          Edit Rule
        </button>
      </div>
    </div>
  );
}